import { createApi } from '@reduxjs/toolkit/query/react';

import { apiBaseUrl } from '../Api';
import { makeBaseQuery } from '../baseQuery';
import type {
  GetUserNotificationsArgs,
  MarkAllUserNotificationsReadArgs,
  NotificationRow,
  Section,
  UserNotificationsResult
} from './notificationTypes';

const asRecord = (value: unknown): Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};

const envelope = (response: unknown): unknown => asRecord(response).data;

const asString = (value: unknown, fallback = ''): string =>
  typeof value === 'string' ? value : fallback;

const asNullableString = (value: unknown): string | null =>
  typeof value === 'string' ? value : null;

const asNumber = (value: unknown, fallback = 0): number =>
  typeof value === 'number' && Number.isFinite(value) ? value : fallback;

const SECTIONS: Section[] = ['seller', 'broker', 'buyer'];

const asSection = (value: unknown): Section | null =>
  SECTIONS.includes(value as Section) ? (value as Section) : null;

function normalizeNotificationRow(value: unknown): NotificationRow {
  const raw = asRecord(value);
  return {
    id: asNumber(raw.id),
    type: asString(raw.type),
    title: asString(raw.title),
    description: asNullableString(raw.description),
    is_read: raw.is_read === true,
    needs_you: raw.needs_you === true,
    link: asNullableString(raw.link),
    section: asSection(raw.section),
    created_at: asString(raw.created_at)
  };
}

export function normalizeUserNotifications(response: unknown): UserNotificationsResult {
  const data = asRecord(envelope(response));
  const items = Array.isArray(data.items) ? data.items.map(normalizeNotificationRow) : [];
  const counts = asRecord(data.counts);
  const unreadCount = asNumber(data.unreadCount);
  return {
    items,
    unreadCount,
    counts: { unread: asNumber(counts.unread, unreadCount), needsYou: asNumber(counts.needsYou) }
  };
}

const toQuery = (params: Record<string, string | boolean | undefined>): string => {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined) continue;
    search.append(key, String(value));
  }
  const qs = search.toString();
  return qs ? `?${qs}` : '';
};

export const notificationApi = createApi({
  reducerPath: 'notificationApi',
  baseQuery: makeBaseQuery(apiBaseUrl),
  tagTypes: ['Notification'],
  endpoints: (builder) => ({
    getUserNotifications: builder.query<UserNotificationsResult, GetUserNotificationsArgs | void>({
      query: (args) => ({
        url: `user/notifications${toQuery({
          section: args?.section,
          unreadOnly: args?.unreadOnly
        })}`
      }),
      transformResponse: (response: unknown) => normalizeUserNotifications(response),
      providesTags: [{ type: 'Notification', id: 'LIST' }]
    }),
    markUserNotificationRead: builder.mutation<void, { id: number }>({
      query: ({ id }) => ({ url: `user/notifications/${id}/read`, method: 'PUT' }),
      invalidatesTags: [{ type: 'Notification', id: 'LIST' }]
    }),
    markAllUserNotificationsRead: builder.mutation<void, MarkAllUserNotificationsReadArgs | void>({
      query: (args) => ({
        url: `user/notifications/read-all${toQuery({ section: args?.section })}`,
        method: 'PUT'
      }),
      invalidatesTags: [{ type: 'Notification', id: 'LIST' }]
    })
  })
});

export const {
  useGetUserNotificationsQuery,
  useMarkUserNotificationReadMutation,
  useMarkAllUserNotificationsReadMutation
} = notificationApi;
